import { PageType } from "@/services/shared-models";
import { CommentsService } from "@/services/user-services";
import { CommentEntry, CreateComment } from "@/services/user-services/models";
import { useFilterStore, useLocaleStore } from "@/stores";
import { useAuth0 } from "@auth0/auth0-react";
import { PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { clsx } from "clsx";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

interface CommentsFormProps {
    open: boolean;
    replyTo?: string;
    pageType: PageType;
    pageSlug?: string;
    onCancel: () => void;
    onSubmit: (comment: CommentEntry) => void;
}

const CommentsForm = ({
    open,
    replyTo,
    pageType,
    pageSlug: contentPageSlug,
    onCancel,
    onSubmit,
}: CommentsFormProps) => {
    const activeRegion = useLocaleStore((state) => state.activeRegion);
    const activeLanguage = useLocaleStore((state) => state.activeLanguage);

    // const versions = useDataStore((state) => state.versions);
    // const activeVersionId = useFilterStore((state) => state.activeVersion);
    // const activeVersion = useMemo(() => versions.find((x) => x.id === activeVersionId), [versions, activeVersionId]);

    const activeDisciplines = useFilterStore((state) => state.activeDisciplines);
    const activeSoftware = useFilterStore((state) => state.activeSoftware);
    const activeIfcVersion = useFilterStore((state) => state.activeIfcVersion);
    const activeUseCases = useFilterStore((state) => state.activeUseCases);

    const { "*": pageSlug } = useParams();
    const slug = pageSlug?.split("/")[1];

    const [name, setName] = useState<string>("");
    const [nameError, setNameError] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [emailError, setEmailError] = useState<string>("");
    const [comment, setComment] = useState<string>("");
    const [commentError, setCommentError] = useState<string>("");
    const [requestNotification, setRequestNotification] = useState<boolean>(true);

    const [loading, setLoading] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);

    const commentsService = useMemo(
        () => new CommentsService(activeRegion!, activeLanguage!),
        [activeRegion, activeLanguage]
    );

    const { isAuthenticated, user } = useAuth0();

    useEffect(() => {
        if (!open) resetForm();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const resetForm = () => {
        if (isAuthenticated && user) {
            setName(user.name!);
            setEmail(user.email!);
        } else {
            setName("");
            setEmail("");
        }

        setNameError("");
        setEmailError("");
        setComment("");
        setCommentError("");
        setRequestNotification(true);
        setLoading(false);
        setSuccess(false);
    };

    const sendComment = () => {
        switch (pageType) {
            case PageType.Content:
                sendContentPageComment();
                break;
            case PageType.Elements:
                sendElementPageComment();
                break;
        }
    };

    const sendElementPageComment = () => {
        if (!validateForm()) return;

        setLoading(true);

        const newComment: CreateComment = {
            username: name,
            email,
            commentBlob: comment,
            requestNotification,
            uiBlob: JSON.stringify({
                disciplines: activeDisciplines,
                useCases: activeUseCases,
                software: [activeSoftware, activeIfcVersion].filter((x) => x),
            }),
            parentId: replyTo,
        };

        commentsService.postOnElementPage(slug!, newComment).then((result) => {
            setLoading(false);
            setSuccess(true);
            onSubmit(result);
        });
    };

    const sendContentPageComment = () => {
        if (!validateForm()) return;

        setLoading(true);

        const newComment: CreateComment = {
            username: name,
            email,
            commentBlob: comment,
            requestNotification,
            uiBlob: "",
            parentId: replyTo,
        };

        commentsService.postOnContentPage(contentPageSlug!, newComment).then((result) => {
            setLoading(false);
            setSuccess(true);
            onSubmit(result);
        });
    };

    const validateForm = () => {
        return validateName(name) && validateEmail(email) && validateComment(comment);
    };

    const validateName = (value: string) => {
        if (value.trim().length === 0) {
            setNameError(
                "Please enter your name." // TODO: translate
            );
            return false;
        }

        setNameError("");
        return true;
    };

    const validateEmail = (value: string) => {
        if (value.trim().length === 0) {
            setEmailError("Please enter a valid email address.");
            return false;
        } else {
            if (!/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(value)) {
                setEmailError("Please enter a valid email address.");
                return false;
            }
        }

        setEmailError("");
        return true;
    };

    const validateComment = (value: string) => {
        if (value.trim().length <= 5) {
            setCommentError("Comment too short.");
            return false;
        }

        setCommentError("");
        return true;
    };

    if (loading) {
        return (
            <div role="status" className="flex items-center justify-center h-52">
                <svg
                    aria-hidden="true"
                    className="inline w-8 h-8 text-primary-500 animate-spin fill-gray-600 "
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                    />
                    <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                    />
                </svg>
                <span className="sr-only">Loading...</span>
            </div>
        );
    }

    return (
        <div
            className={clsx([
                "transition-all duration-300 ease-in-out overflow-hidden print:hidden",
                open ? "max-h-[2000px] opacity-100 p-4" : "max-h-0 opacity-0",
            ])}
        >
            <div className="bg-white rounded-md ring shadow-lg ring-[#E5E7EB]">
                {!loading && success && (
                    <div className="flex h-52 items-center justify-center flex-col">
                        <div className="text-center">
                            <p className="text-gray-700">{"Thank you for your comment."}</p>
                        </div>
                    </div>
                )}

                {!loading && !success && (
                    <>
                        <div className="p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="col-span-1">
                                    <div className="mb-4">
                                        <label htmlFor="name" className="block font-medium text-gray-700 mb-1">
                                            Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            id="name"
                                            type="text"
                                            required
                                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                                                nameError
                                                    ? "border-red-500 focus:ring-red-500"
                                                    : "border-gray-300 focus:ring-primary-500"
                                            }`}
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            onBlur={(e) => Boolean(nameError) && validateName(e.target.value)}
                                        />
                                        {nameError && <p className="mt-1 text-red-500">{nameError}</p>}
                                    </div>
                                </div>
                                <div className="col-span-1">
                                    <div className="mb-4">
                                        <label htmlFor="email" className="block font-medium text-gray-700 mb-1">
                                            Email <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            id="email"
                                            type="email"
                                            required
                                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                                                emailError
                                                    ? "border-red-500 focus:ring-red-500"
                                                    : "border-gray-300 focus:ring-primary-500"
                                            }`}
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            onBlur={(e) => Boolean(emailError) && validateEmail(e.target.value)}
                                        />
                                        {emailError && <p className="mt-1 text-red-500">{emailError}</p>}
                                    </div>
                                </div>
                                <div className="col-span-1 md:col-span-2">
                                    <div className="mb-4">
                                        <label htmlFor="comment" className="block font-medium text-gray-700 mb-1">
                                            Comment <span className="text-red-500">*</span>
                                        </label>
                                        <textarea
                                            id="comment"
                                            required
                                            rows={6}
                                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                                                commentError
                                                    ? "border-red-500 focus:ring-red-500"
                                                    : "border-gray-300 focus:ring-primary-500"
                                            }`}
                                            value={comment}
                                            onChange={(e) => setComment(e.target.value)}
                                            onBlur={(e) => Boolean(commentError) && validateComment(e.target.value)}
                                        />
                                        {commentError && <p className="mt-1 text-red-500">{commentError}</p>}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="px-4 pb-4 flex flex-col md:flex-row">
                            <div className="flex-1 items-center">
                                {!replyTo && (
                                    <div className="flex items-center mb-4 md:mb-0">
                                        <div className="group grid size-6 grid-cols-1 shrink-0">
                                            <input
                                                id="notification"
                                                type="checkbox"
                                                checked={requestNotification}
                                                onChange={(e) => setRequestNotification(e.target.checked)}
                                                className={clsx([
                                                    "col-start-1 row-start-1 appearance-none rounded-lg border-2 min-w-6 min-h-6",
                                                    "border-primary-300 bg-primary-100",
                                                    "checked:bg-white checked:outline-2 checked:-outline-offset-4 checked:outline-primary-500 cursor-pointer",
                                                ])}
                                            />
                                            <svg
                                                fill="none"
                                                viewBox="0 0 14 14"
                                                className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-primary-500 group-has-disabled:stroke-gray-950/25"
                                            >
                                                <path
                                                    d="M3 8L6 11L11 3.5"
                                                    strokeWidth={2}
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    className="opacity-0 group-has-checked:opacity-100"
                                                />
                                                <path
                                                    d="M3 7H11"
                                                    strokeWidth={2}
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    className="opacity-0 group-has-indeterminate:opacity-100"
                                                />
                                            </svg>
                                        </div>
                                        <label htmlFor="notification" className="ml-2 block text-gray-700">
                                            Notify me when someone replies to my comment.
                                        </label>
                                    </div>
                                )}
                            </div>
                            <div className="flex justify-end gap-2">
                                <button
                                    type="button"
                                    className="inline-flex items-center px-4 py-2 border border-transparent font-medium rounded-md shadow-sm cursor-pointer text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                    onClick={() => sendComment()}
                                >
                                    <PaperAirplaneIcon className="w-5 h-5 mr-2" />
                                    Submit
                                </button>

                                <button
                                    type="button"
                                    className="inline-flex items-center px-4 py-2 border border-transparent font-medium rounded-md shadow-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                    onClick={onCancel}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CommentsForm;
